import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import { TableBody, Table, TableCell, TableRow, Paper, Grid, Container } from '@mui/material';
import EmployeeService from '@/services/Employee.service';
import CommonActionButton from '@/components/common/CommonActionButton';
import HookFormControls, { HookFormControlsProps } from '@/components/common/HookFormControls';
import Loading from '@/components/common/Loading';
import PageTitle from '@/components/common/PageTitle';
import { useAuth } from '@/common/hooks/useAuth';
import { Messages } from '@/common/constants';
import { Departments } from '@/types/Department.type';
import Employee from '@/types/Employee.type';

const Profile: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const useFormMethods = useForm<Employee>({ shouldUnregister: false });
  const [employee, setEmployee] = useState<Employee & Departments>();
  const [isLoading, setIsLoading] = useState(true);
  const auth = useAuth();
  const {
    handleSubmit,
    getValues,
    formState: { isSubmitting },
    reset,
    watch,
    trigger,
  } = useFormMethods;

  useEffect(() => {
    getEmployee();
  }, []);

  const getEmployee = async () => {
    try {
      const response = await EmployeeService.get(auth.user.id);
      setIsLoading(false);
      response.data.extensionNumber = response.data.extensionNumber ?? '';
      response.data.phoneNumber = response.data.phoneNumber ?? '';
      reset(response.data);
      setEmployee(response.data);
    } catch (error: any) {
      setIsLoading(false);
      enqueueSnackbar(Messages.select.failure);
    }
  };

  const onSubmit = async (values: Employee) => {
    try {
      await EmployeeService.updateProfile(values);
      enqueueSnackbar(Messages.update.success, { variant: 'info' });
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  const phoneNumber: HookFormControlsProps = {
    name: 'phoneNumber',
    type: 'text',
    label: '携帯電話',
    rule: {
      maxLength: {
        value: 15,
        message: '携帯電話は15字以内で入力してください。',
      },
      /*pattern: {
        value: new RegExp('^0[0-9]{9,14}$'),
        message: '携帯電話は0から始まる、10~15桁の数字で入力してください。',
      },*/
    },
  };

  const extensionNumber: HookFormControlsProps = {
    name: 'extensionNumber',
    type: 'text',
    label: '内線番号',
    rule: {
      maxLength: {
        value: 15,
        message: '内線番号は15文字以内で入力してください。',
      },
      /*pattern: {
        value: new RegExp('^[0-9]+$'),
        message: '内線番号は数字で入力してください。',
      },*/
    },
  };

  const autoUpdateTime: HookFormControlsProps = {
    name: 'autoUpdateTime',
    type: 'text',
    label: '自動更新時間（秒）',
    helper: '自動更新時間（秒）に入力した間隔で行先一覧を更新します。未入力の場合は300秒間隔で更新されます。',
    rule: {
      pattern: {
        value: new RegExp('(^[1-9]([0-9]+)$|^[0-9]$)'),
        message: '更新時間（秒）は整数で入力してください。',
      },
      max: {
        value: 99999,
        message: '更新時間（秒）は10~99999の範囲で設定してください。',
      },
      min: {
        value: 10,
        message: '更新時間（秒）は10~99999の範囲で設定してください。',
      },
    },
  };

  if (isLoading) {
    return <Loading />;
  }
  return (
    <Container component="main" maxWidth="md">
      <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
        <PageTitle>個人設定</PageTitle>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 0, md: 0 } }}>
          <FormProvider {...useFormMethods}>
            <Table aria-label="simple table">
              <TableBody>
                <TableRow>
                  <TableCell align="left">
                    <Grid>氏名　{employee?.name}</Grid>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <Grid container alignItems='center' direction="row">
                      <Grid>
                        部署　
                      </Grid>
                      <Grid>
                      {employee?.departments.map((department) => (
                        <React.Fragment key={department.id}>
                          {department.name}
                          <br />
                        </React.Fragment>
                      ))}
                      </Grid>
                    </Grid>

                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <HookFormControls {...extensionNumber} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <Grid>
                      <HookFormControls {...phoneNumber} />
                    </Grid>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">
                    <Grid>
                      <HookFormControls {...autoUpdateTime} />
                    </Grid>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <CommonActionButton isSubmitting={isSubmitting} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </FormProvider>
        </Paper>
      </form>
    </Container>
  );
};
export default Profile;
